import {
	FC,
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useState,
} from 'react';
import { Backdrop, Portal, useTheme } from '@mui/material';
import { animated } from '@react-spring/web';

import { TSpringConfig, useBottomSheetSnap } from './hooks/useBottomSheetSnap';
import { SnapPoint } from './SnapPoint';
import {
	DragHandle,
	DragHandleArea,
	DrawerContentArea,
	DrawerHeaderSlot,
	DrawerStyled,
	FooterArea,
} from './styles';

export interface IMplSnappingBottomSheetProps {
	/** Snap points, e.g. [SnapPoint.vh(0.3), SnapPoint.px(400)]. SnapPoint needs to memoized. */
	snapPoints: SnapPoint[];
	/** Currently active snap index (controlled) */
	currentSnapIndex: number;
	/** Called when drag gesture resolves to a different snap index */
	onSnapIndexChange: (index: number) => void;
	/** Header slot — rendered between drag handle and content. Also draggable. */
	header?: ReactNode;
	/** Main scrollable content */
	children: ReactNode;
	/** Footer slot — e.g. an action button */
	footer?: ReactNode;
	/** Whether footer renders with shadow + rounded top corners instead of border-top. Default: false */
	elevatedFooter?: boolean;
	/** Whether to animate from bottom (0vh) to initialSnapIndex on mount. Default: true */
	animateOnMount?: boolean;
	/** Whether the bottom sheet is open */
	open: boolean;
	/** Spring animation config. Defaults to { tension: 300, friction: 30 } */
	animationConfig?: Partial<TSpringConfig>;
	/** Whether to unmount the component after close animation completes. Default: false */
	unmountOnClose?: boolean;
	/** Show an animated backdrop overlay behind the sheet. Default: false */
	showBackdrop?: boolean;
	/** Called when the backdrop is clicked */
	onBackdropClick?: () => void;
	/** Custom z-index for the sheet and its backdrop. Useful when stacking multiple sheets. */
	zIndex?: number;
	/** Ref forwarded to the scrollable content area. Useful for scroll-position logic. */
	contentRef?: RefObject<HTMLDivElement>;
	/** Hide the scrollbar on the content area without disabling scroll. */
	hideScrollbar?: boolean;
	/** className forwarded to the underlying Drawer root — enables external styling (e.g. via `styled()`). */
	className?: string;
	dragHandleTestId?: string;
}

export const MplSnappingBottomSheet: FC<IMplSnappingBottomSheetProps> = ({
	snapPoints,
	currentSnapIndex,
	onSnapIndexChange,
	header,
	children,
	footer,
	elevatedFooter = false,
	animateOnMount = true,
	open,
	unmountOnClose = false,
	showBackdrop = false,
	onBackdropClick,
	animationConfig,
	zIndex,
	contentRef,
	hideScrollbar,
	className,
	dragHandleTestId = 'bottomSheet-dragHandle-area',
}) => {
	const theme = useTheme();
	const [shouldRender, setShouldRender] = useState(open);

	const _zIndex = zIndex ?? theme.zIndex.drawer;

	/** When open becomes true, render immediately */
	useEffect(() => {
		if (open) {
			setShouldRender(true);
		}
	}, [open]);

	const handleCloseAnimationComplete = useCallback(() => {
		if (unmountOnClose) {
			setShouldRender(false);
		}
	}, [unmountOnClose]);

	const { springs, bind } = useBottomSheetSnap({
		snapPoints,
		currentSnapIndex,
		onSnapIndexChange,
		animateOnMount,
		open,
		onCloseAnimationComplete: handleCloseAnimationComplete,
		animationConfig,
	});

	return (
		<Portal>
			{showBackdrop && (
				<Backdrop
					open={open}
					onClick={onBackdropClick}
					sx={{
						zIndex: _zIndex - 1,
					}}
				/>
			)}
			{shouldRender && (
				<DrawerStyled
					variant="persistent"
					anchor="bottom"
					open={open}
					className={className}
					sx={{
						'& .MuiPaper-root': {
							zIndex: _zIndex,
						},
					}}
					PaperProps={{
						component: animated.div,
						style: springs as any,
					}}
				>
					<DragHandleArea {...bind()} data-testid={dragHandleTestId}>
						<DragHandle />
					</DragHandleArea>

					{header && <DrawerHeaderSlot {...bind()}>{header}</DrawerHeaderSlot>}

					<DrawerContentArea
						ref={contentRef}
						hideScrollbar={hideScrollbar}
						className="drawerContent"
					>
						{children}
					</DrawerContentArea>

					{footer && (
						<FooterArea elevated={elevatedFooter}>{footer}</FooterArea>
					)}
				</DrawerStyled>
			)}
		</Portal>
	);
};
